import { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Hooks
import { useMenuItem } from 'hooks/UseMenuItem';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Constants
import { BonusTypesLabels, BonusTypesIcons, l, BonusStateLabels, AwardUsageTypeLabels } from 'constants/common';
// Import Services
import { Helpers } from 'services';

const { customMomentWithoutTimezoneConversion } = Helpers;

const GeneralInfo = ({
    id,
    title,
    description,
    type,
    startDate,
    endDate,
    state,
    walletType,
    internalMessageTitle,
    wonderWheelId,
    wonderWheelName,
    nftVId,
    nftVName,
    usageType,
    isLocalNetwork,
    sectionTitle,
}) => {
    const { t } = useTranslation();

    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const getContent = (data) => {
        return isNil(data)
            ? t(l.NA)
            : customMomentWithoutTimezoneConversion(data).format(partnerDateTimeWithoutSecondsFormat);
    };

    const { convertedData: BalanceTypesData, isLoading: isLoadingBalanceTypesData } = useMenuItem(106);

    return (
        <>
            <div className="crm-object-modal-head">
                <div className="title-wrapper">
                    <p className="title-wrapper-text">{sectionTitle ? t(sectionTitle) : t(l.GeneralInfo)}</p>
                    {isNil(sectionTitle) && <span className="title-wrapper-line" />}
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.ID)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{id}</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Title)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{title}</span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Description)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {isEmpty(description) ? t(l.NA) : description}
                    </span>
                </div>
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.Type)}</span>
                    <div className="ellipsis-text kv-value kv-value-css">
                        <div className="crm-object-info-item">
                            {!isNil(BonusTypesIcons[type]) && <Icon type={BonusTypesIcons[type]} />}
                            <p>{t(BonusTypesLabels[type])}</p>
                        </div>
                    </div>
                </div>
                {type === BonusTypesLabels.WonderWheelSpin && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.WonderWheelId)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{wonderWheelId}</span>
                    </div>
                )}
                {type === BonusTypesLabels.WonderWheelSpin && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.WonderWheelName)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{wonderWheelName}</span>
                    </div>
                )}
                {type === BonusTypesLabels.NftVoucherBonus && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.NftVoucherId)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{nftVId}</span>
                    </div>
                )}
                {type === BonusTypesLabels.NftVoucherBonus && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.NftVoucherName)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{nftVName}</span>
                    </div>
                )}
                {type === BonusTypesLabels.PiePoints && (
                    <>
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.UsageType)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {AwardUsageTypeLabels[usageType]}
                            </span>
                        </div>
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.IsLocalNetwork)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {isLocalNetwork ? t(l.Yes) : t(l.No)}
                            </span>
                        </div>
                    </>
                )}
                {!isNil(state) && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.State)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{t(BonusStateLabels[state])}</span>
                    </div>
                )}

                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.StartDate)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{getContent(startDate)}</span>
                </div>

                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.EndDate)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{getContent(endDate)}</span>
                </div>

                {!isEmpty(walletType) && !isLoadingBalanceTypesData && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.WalletType)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {(BalanceTypesData as AnyObject[]).find((item) => item.value === walletType)?.label}
                        </span>
                    </div>
                )}
                {!isNil(internalMessageTitle) && (
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.InternalMessageTitle)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{internalMessageTitle}</span>
                    </div>
                )}
            </div>
        </>
    );
};

GeneralInfo.propTypes = {
    id: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    type: PropTypes.number,
    state: PropTypes.number,
    walletType: PropTypes.string,
    internalMessageTitle: PropTypes.string,
    wonderWheelId: PropTypes.string,
    wonderWheelName: PropTypes.string,
    nftVId: PropTypes.number,
    nftVName: PropTypes.string,
    usageType: PropTypes.number,
    isLocalNetwork: PropTypes.bool,
    sectionTitle: PropTypes.string,
};

GeneralInfo.defaultProps = {
    walletType: '',
    internalMessageTitle: null,
    sectionTitle: null,
};

export default memo(GeneralInfo);
